import { isPast } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import parseISO from 'date-fns/parseISO';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import { sequenceTOption } from '../../function';
import { FullMatchFragment } from '../../generated/graphql';
import { dateFormats } from '../../utils/dateFormats';
import { imageSize } from '../../utils/imagekit';
import { staticMap } from '../../utils/mapbox';

import { getScoreInfo } from './utils';

export const getOpenGraphItems = (match: FullMatchFragment) => {
  const { homeTeam, awayTeam } = getScoreInfo(match);
  const playedAt = parseISO(match.playedAt);

  const getUpcomingDesc = () => {
    return `${match.team.name}'s match against ${
      match.opponent
    } at ${formatInTimeZone(
      playedAt,
      match.timeZone,
      dateFormats.time,
    )} on ${formatInTimeZone(
      playedAt,
      match.timeZone,
      dateFormats.shortDate,
    )}.`;
  };

  const getPastDesc = () =>
    pipe(
      sequenceTOption(
        O.fromNullable(match.goalsFor),
        O.fromNullable(match.goalsAgainst),
      ),
      O.map(([goalsFor, goalsAgainst]) => {
        const resultDesc =
          goalsFor > goalsAgainst
            ? 'win over'
            : goalsFor < goalsAgainst
            ? 'loss against'
            : 'draw with';

        return `${
          match.team.name
        }'s ${goalsFor}-${goalsAgainst} ${resultDesc} ${
          match.opponent
        } on ${formatInTimeZone(
          playedAt,
          match.timeZone,
          dateFormats.shortDate,
        )}.`;
      }),
      O.getOrElse(
        () =>
          `${match.team.name}'s match with ${
            match.opponent
          } on ${formatInTimeZone(
            playedAt,
            match.timeZone,
            dateFormats.shortDate,
          )}.`,
      ),
    );

  const description = isPast(playedAt) ? getPastDesc() : getUpcomingDesc();

  const title = `Matchhaus - ${homeTeam} vs ${awayTeam}`;
  const image = getMatchImage(match);

  return { title, description, image };
};

const getMatchImage = (match: FullMatchFragment) => {
  if (match.image) {
    return imageSize(match.image.url, 'og');
  }

  if (match.location) {
    return staticMap({ latlng: [match.location.lat, match.location.lng] });
  }

  return `${window.location.protocol}//${window.location.host}/assets/og-image.png`;
};
